import { useRef } from 'react';
import { toast } from 'sonner';
import { CopyIcon, MailIcon, Share2Icon, XIcon } from 'lucide-react';

import { useLanguage } from '@/contexts/language-context';
import { Button } from '@/components/ui/button';
import { copyText } from '@/lib/utils';
import type { EmployeeInviteLinkDto } from '@/lib/api/employees';

interface Props {
  link: EmployeeInviteLinkDto;
  /** The employee's email on file — prefilled as the mailto recipient. */
  recipientEmail: string | null;
  onClose: () => void;
}

const canNativeShare = () =>
  typeof navigator !== 'undefined' && typeof navigator.share === 'function';

// Shown after `POST .../employees/{id}/invite` succeeds. The backend returns
// the link only — we compose the delivery here — copy, native share sheet,
// and prefilled mailto:. The link is a sign-in credential, so it's never
// logged, and unlike a quote share-link it has no expiry to display.
export function InviteLinkPanel({ link, recipientEmail, onClose }: Props) {
  const { t } = useLanguage();
  const inputRef = useRef<HTMLInputElement>(null);

  const body = `${t.employeeInviteEmailBody}\n\n${link.link}`;
  const mailtoHref = `mailto:${recipientEmail ?? ''}?subject=${encodeURIComponent(
    t.employeeInviteEmailSubject,
  )}&body=${encodeURIComponent(body)}`;

  async function handleCopy() {
    const ok = await copyText(link.link);
    if (ok) {
      toast.success(t.employeeInviteLinkCopied);
    } else {
      inputRef.current?.select();
      toast.error(t.employeeInviteLinkCopyFailed);
    }
  }

  async function handleNativeShare() {
    try {
      await navigator.share({
        title: t.employeeInviteEmailSubject,
        text: t.employeeInviteEmailBody,
        url: link.link,
      });
    } catch (err) {
      // User dismissed the share sheet — not an error.
      if (err instanceof Error && err.name === 'AbortError') return;
      const ok = await copyText(link.link);
      if (ok) toast.success(t.employeeInviteLinkCopied);
    }
  }

  return (
    <div
      className="flex flex-col gap-3 border-t pt-4"
      data-testid="employee-invite-panel"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-medium">{t.employeeInviteLinkPanelTitle}</p>
          <p className="text-xs text-muted-foreground">{t.employeeInviteLinkHint}</p>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label={t.employeeInviteClose}
          className="text-muted-foreground hover:text-foreground transition-colors rounded-md p-1"
          data-testid="employee-invite-panel-close"
        >
          <XIcon className="w-4 h-4" />
        </button>
      </div>

      <div className="flex gap-2">
        <input
          ref={inputRef}
          type="text"
          readOnly
          value={link.link}
          onFocus={(e) => e.currentTarget.select()}
          data-testid="employee-invite-url"
          className="flex-1 min-w-0 px-3 py-2 text-sm border rounded-md bg-muted/40 focus:outline-none focus:ring-2 focus:ring-ring"
        />
        <Button
          type="button"
          size="sm"
          onClick={handleCopy}
          data-testid="employee-invite-copy"
        >
          <CopyIcon className="w-3.5 h-3.5" />
          {t.employeeInviteCopy}
        </Button>
      </div>

      <div className="flex flex-wrap gap-2">
        {canNativeShare() && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={handleNativeShare}
            data-testid="employee-invite-native"
          >
            <Share2Icon className="w-3.5 h-3.5" />
            {t.employeeInviteNative}
          </Button>
        )}
        <Button asChild size="sm" variant="outline" data-testid="employee-invite-email">
          <a href={mailtoHref}>
            <MailIcon className="w-3.5 h-3.5" />
            {t.employeeInviteEmail}
          </a>
        </Button>
      </div>

      <p className="text-xs text-muted-foreground">{t.employeeInviteExpiryNote}</p>
    </div>
  );
}
